import { getFallbackPathForRole, isForecastViewerRole, isManufacturingBlockedRole, isPlanningVisibleManufacturingPath, isSuperAdmin } from '@/permissions';
import { ErrorBoundary } from '@components/common/ErrorBoundary';
import { SessionRenewalPrompt } from '@components/common/SessionRenewalPrompt';
import { PageSkeleton } from '@components/ui/PageSkeleton';
import { useSettings } from '@hooks/useSettings';
import { useAuthStore } from '@stores/auth.store';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

// Layout
const AuthLayout = lazy(() => import('@components/layout/AuthLayout'));
const MainLayout = lazy(() => import('@components/layout/MainLayout'));

// Pages
const Dashboard = lazy(() => import('@pages/Dashboard'));
const NotFound = lazy(() => import('@pages/NotFound'));
const ForgotPassword = lazy(() => import('@pages/auth/ForgotPassword'));
const Login = lazy(() => import('@pages/auth/Login'));
const ForceResetPassword = lazy(() => import('@pages/auth/ForceResetPassword'));
const Actuals = lazy(() => import('@pages/data/Actuals'));
const DataImport = lazy(() => import('@pages/data/DataImport'));
const Dimensions = lazy(() => import('@pages/data/Dimensions'));
const ProductMaster = lazy(() => import('@pages/data/ProductMaster'));
const Locations = lazy(() => import('@pages/data/Locations'));
const ForecastDashboard = lazy(() => import('@pages/forecasts/ForecastDashboard'));
const ForecastDetail = lazy(() => import('@pages/forecasts/ForecastDetail'));
const Forecasts = lazy(() => import('@pages/forecasts/Forecasts'));
const ManufacturingRoutes = lazy(() => import('@pages/manufacturing/ManufacturingRoutes'));
const CreatePlan = lazy(() => import('@pages/plans/CreatePlan'));
const PlanDetail = lazy(() => import('@pages/plans/PlanDetail'));
const Plans = lazy(() => import('@pages/plans/Plans'));
const Reports = lazy(() => import('@pages/reports/Reports'));
const AiReporting = lazy(() => import('@pages/reports/AiReporting'));
const InsightsDashboard = lazy(() => import('@pages/insights/InsightsDashboard'));
const PharmaReportsRoutes = lazy(() => import('@pages/pharma-reports/PharmaReportsRoutes'));
const Scenarios = lazy(() => import('@pages/scenarios/Scenarios'));
const AuditLog = lazy(() => import('@pages/settings/AuditLog'));
const Notifications = lazy(() => import('@pages/settings/Notifications'));
const Profile = lazy(() => import('@pages/settings/Profile'));
const MargEde = lazy(() => import('@pages/settings/MargEde'));
const Settings = lazy(() => import('@pages/settings/Settings'));
const Users = lazy(() => import('@pages/settings/Users'));
const Roles = lazy(() => import('@pages/settings/Roles'));
const PlatformDashboard = lazy(() => import('@pages/platform/PlatformDashboard'));
const TenantManage = lazy(() => import('@pages/platform/TenantManage'));
const AiBilling = lazy(() => import('@pages/billing/AiBilling'));
const AiBillingAdmin = lazy(() => import('@pages/platform/AiBillingAdmin'));

function RouteFallback() {
  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 p-4">
      <PageSkeleton />
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900 p-4">
        <PageSkeleton />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Force password reset before accessing any protected route
  if (user?.mustResetPassword) {
    return <Navigate to="/force-reset-password" replace />;
  }

  return <>{children}</>;
}

// Public Route Component (redirect to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to={getFallbackPathForRole(user?.role)} replace />;
  }

  return <>{children}</>;
}

function HomeRoute() {
  const role = useAuthStore((s) => s.user?.role);
  return <Navigate to={getFallbackPathForRole(role)} replace />;
}

function RoleAwareRoute({
  restrictForecastViewer = false,
  restrictManufacturing = false,
  requiredModule,
  children,
}: {
  restrictForecastViewer?: boolean;
  restrictManufacturing?: boolean;
  requiredModule?: string;
  children: React.ReactNode;
}) {
  const role = useAuthStore((s) => s.user?.role);
  const { data: settings, isLoading: isSettingsLoading } = useSettings();
  const location = useLocation();

  // SUPER_ADMIN is never blocked
  if (isSuperAdmin(role)) return <>{children}</>;

  // Forecast planner/viewer roles get read-only access to specific planning-relevant
  // manufacturing surfaces (PO, PI, Inventory, Suppliers, Batches) — bypass the
  // restrictManufacturing guard for those paths.
  const planningPathException =
    restrictManufacturing &&
    isManufacturingBlockedRole(role) &&
    isPlanningVisibleManufacturingPath(location.pathname);

  const blocked =
    (restrictForecastViewer && isForecastViewerRole(role) && !planningPathException) ||
    (restrictManufacturing && isManufacturingBlockedRole(role) && !planningPathException);

  if (!blocked && requiredModule && isSettingsLoading) {
    return <RouteFallback />;
  }

  const moduleDisabled = Boolean(
    requiredModule &&
    settings?.enabledModules &&
    settings.enabledModules[requiredModule as keyof NonNullable<typeof settings.enabledModules>] === false,
  );

  if (blocked || moduleDisabled) {
    return <Navigate to={getFallbackPathForRole(role)} replace />;
  }

  return <>{children}</>;
}

function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role);
  if (!isSuperAdmin(role)) {
    return <Navigate to={getFallbackPathForRole(role)} replace />;
  }
  return <>{children}</>;
}

function TenantOnlyRoute({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role);
  if (isSuperAdmin(role)) {
    return <Navigate to="/platform" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  useEffect(() => {
    void useAuthStore.getState().checkAuth();
  }, []);

  return (
    <ErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public routes */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        {/* Force password reset — authenticated but must change password */}
        <Route path="/force-reset-password" element={<ForceResetPassword />} />
      </Route>

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <TenantOnlyRoute>
              <MainLayout />
            </TenantOnlyRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<HomeRoute />} />
        <Route path="/dashboard" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />

        {/* Plans */}
        <Route path="/plans" element={<Plans />} />
        <Route path="/plans/new" element={<CreatePlan />} />
        <Route path="/plans/:id" element={<PlanDetail />} />

        {/* Forecasts */}
        <Route path="/forecasts" element={<ErrorBoundary><Forecasts /></ErrorBoundary>} />
        <Route path="/forecasts/dashboard" element={<ErrorBoundary><ForecastDashboard /></ErrorBoundary>} />
        <Route path="/forecasts/:id" element={<ErrorBoundary><ForecastDetail /></ErrorBoundary>} />

        {/* Scenarios */}
        <Route path="/scenarios" element={<Scenarios />} />

        {/* Data Management */}
        <Route path="/data/import" element={<RoleAwareRoute restrictForecastViewer requiredModule="data"><DataImport /></RoleAwareRoute>} />
        <Route path="/data/actuals" element={<RoleAwareRoute restrictForecastViewer requiredModule="data"><Actuals /></RoleAwareRoute>} />
        <Route path="/data/dimensions" element={<RoleAwareRoute restrictForecastViewer requiredModule="data"><Dimensions /></RoleAwareRoute>} />
        <Route path="/data/products" element={<RoleAwareRoute restrictForecastViewer requiredModule="data"><ProductMaster /></RoleAwareRoute>} />
        <Route path="/data/locations" element={<RoleAwareRoute restrictForecastViewer requiredModule="data"><Locations /></RoleAwareRoute>} />

        {/* Reports */}
        <Route path="/reports" element={<Reports />} />
        <Route path="/reports/ai" element={<RoleAwareRoute requiredModule="ai-reporting"><AiReporting /></RoleAwareRoute>} />
        <Route path="/insights" element={<RoleAwareRoute requiredModule="ai-reporting"><InsightsDashboard /></RoleAwareRoute>} />
        <Route path="/billing/ai" element={<RoleAwareRoute requiredModule="ai-reporting"><AiBilling /></RoleAwareRoute>} />
        <Route path="/pharma-reports/*" element={<RoleAwareRoute requiredModule="reports"><PharmaReportsRoutes /></RoleAwareRoute>} />

        {/* Settings */}
        <Route path="/settings" element={<RoleAwareRoute restrictForecastViewer><Settings /></RoleAwareRoute>} />
        <Route path="/settings/users" element={<SuperAdminRoute><Users /></SuperAdminRoute>} />
        <Route path="/settings/roles" element={<SuperAdminRoute><Roles /></SuperAdminRoute>} />
        <Route path="/settings/marg-ede" element={<RoleAwareRoute restrictForecastViewer requiredModule="marg-ede"><MargEde /></RoleAwareRoute>} />
        <Route path="/settings/profile" element={<Profile />} />
        <Route path="/settings/audit-log" element={<RoleAwareRoute restrictForecastViewer><AuditLog /></RoleAwareRoute>} />
        <Route path="/notifications" element={<Notifications />} />

        {/* Manufacturing */}
        <Route path="/manufacturing/*" element={<RoleAwareRoute restrictManufacturing requiredModule="manufacturing"><ManufacturingRoutes /></RoleAwareRoute>} />
      </Route>

      <Route
        element={
          <ProtectedRoute>
            <SuperAdminRoute>
              <MainLayout />
            </SuperAdminRoute>
          </ProtectedRoute>
        }
      >
        <Route path="/platform" element={<PlatformDashboard />} />
        <Route path="/platform/tenants/:id" element={<TenantManage />} />
        <Route path="/platform/ai-billing" element={<AiBillingAdmin />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    <SessionRenewalPrompt />
    </Suspense>
    </ErrorBoundary>
  );
}
